import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { canEdit, canView } from '../../utils/permissions';
import { getImportDefs } from '../../api/imports';
import type { ImportDefSummary } from '../../types/Import';
import { t } from '../../i18n/t';
import { ImportRunPanel } from './ImportRunPanel';
import { ImportDefinePanel } from './ImportDefinePanel';
import './GenericImportPage.css';
import { showErrorToast, describeError } from '../../components/ErrorToast';

export function GenericImportPage() {
  const { user } = useAuth();
  const mayRun = canView(user, 'imports');
  const mayDefine = canEdit(user, 'imports');

  const [defs, setDefs] = useState<ImportDefSummary[]>([]);
  const [loadingDefs, setLoadingDefs] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadDefs = useCallback(() => {
    setLoadingDefs(true);
    return getImportDefs()
      .then((data) => {
        setDefs(data);
        setError(null);
      })
      .catch((e: unknown) => {
        showErrorToast(e, { title: t('errorToast.titleLoad') });
        setError(describeError(e).body);
      })
      .finally(() => {
        setLoadingDefs(false);
      });
  }, []);

  useEffect(() => {
    loadDefs();
  }, [loadDefs]);

  return (
    <div className="gimport">
      <h1 className="gimport__title">{t('imports.pageTitle')}</h1>

      {error !== null && (
        <div className="gimport-result gimport-result--error" role="alert">
          {t('imports.common.error', { message: error })}
        </div>
      )}

      {mayRun && <ImportRunPanel defs={defs} loadingDefs={loadingDefs} />}

      {mayDefine && <ImportDefinePanel defs={defs} onChanged={loadDefs} />}
    </div>
  );
}
